/**
 * Заведение кабинета по приглашению платформы.
 *
 * Публичной регистрации у кабинета нет: за каждым брендом стоит
 * договорённость с живым человеком. Раньше это значило, что пароль уезжал
 * партнёру в переписке и оставался в ней навсегда. Теперь платформа выдаёт
 * одноразовую ссылку, а пароль партнёр придумывает сам — его не знает никто,
 * включая нас.
 *
 * Адрес почты здесь показан, но не редактируется: он взят из приглашения.
 * Поле ввода означало бы, что ссылка, уехавшая не туда, открывает кабинет
 * любому, кто подставит свой адрес.
 */
import type { InvitePreview } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Button, Card, Field, Logo, fmtShortDay } from '@allswipes/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { useConsole } from '../session';

const ROLE_NAMES: Record<string, string> = {
  owner: 'владелец — всё, включая профиль бренда',
  manager: 'менеджер — акции и розыгрыши',
  operator: 'оператор — выдача призов',
};

export function InviteScreen() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { refresh } = useConsole();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [checking, setChecking] = useState(true);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void api
      .invitePreview(token)
      .then((p) => alive && setPreview(p))
      .catch((err) =>
        alive && setError(err instanceof ApiError ? err.message : 'Ссылка не работает'),
      )
      .finally(() => alive && setChecking(false));
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.acceptInvite(token, name, password);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не получилось завести кабинет');
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="login">
        <Card className="box raised">
          <span className="sub">Проверяем ссылку…</span>
        </Card>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="login">
        <Card className="box raised">
          <div style={{ marginBottom: 14 }}>
            <Logo height={20} />
          </div>
          <div className="h2">Ссылка не работает</div>
          <p className="sub" style={{ margin: '8px 0 16px' }}>
            Она могла быть использована, отозвана или просрочена — такие ссылки живут две недели.
            Напиши нам, и мы выдадим новую.
          </p>
          <Button block onClick={() => navigate('/login', { replace: true })}>
            К входу
          </Button>
        </Card>
      </div>
    );
  }

  // Пароль проверяем здесь же, а не только на сервере: человек должен видеть
  // причину до нажатия, а не после.
  const short = password.length > 0 && password.length < 10;
  const mismatch = again.length > 0 && again !== password;
  const ready = name.trim() !== '' && password.length >= 10 && again === password;

  return (
    <div className="login">
      <Card className="box raised">
        <div style={{ marginBottom: 14 }}>
          <Logo height={20} />
          <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>
            кабинет партнёра
          </div>
        </div>

        <div className="h2">{preview.brand_name}</div>
        <p className="sub" style={{ margin: '8px 0 16px' }}>
          Платформа завела кабинет для этой компании. Придумай пароль — мы его не узнаем и не
          сможем подсказать. Ссылка одноразовая и действует до {fmtShortDay(preview.expires_at)}.
        </p>

        <Field label="Почта">
          <input className="input" value={preview.email} readOnly />
        </Field>
        <p className="sub" style={{ fontSize: 11.5, margin: '-6px 0 12px' }}>
          Адрес взят из приглашения: под ним ты будешь входить. {ROLE_NAMES[preview.role] ?? preview.role}.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) void submit();
          }}
        >
          <Field label="Как тебя зовут">
            <input
              className="input"
              autoComplete="name"
              placeholder="Шах"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Пароль" error={short ? 'не короче десяти символов' : undefined}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <Field label="Пароль ещё раз" error={mismatch ? 'пароли не совпадают' : undefined}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
            />
          </Field>

          {error && (
            <p className="sub" style={{ color: 'var(--bad)', marginBottom: 10 }}>
              {error}
            </p>
          )}

          <Button variant="pri" block type="submit" disabled={busy || !ready}>
            {busy ? 'Заводим кабинет…' : 'Завести кабинет'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
